import { CartItem } from '@/models/cart/cart-item'
import { isAfter, isBefore, isEqual } from 'date-fns'

import * as CurrentTime from '@/models/current-time/functions'
import Calculator from './coupon/calculator/calculator'
import NoDiscountCalculator from './coupon/calculator/noDiscountCalculator'
import RateCalculator from './coupon/calculator/rateCalculator'
import SubtractionCalculator from './coupon/calculator/subtractionCalculator'
import LabelMaker from './coupon/label/labelMaker'
import NoDiscountLabelMaker from './coupon/label/noDiscountLabelMaker'
import RateLabelMaker from './coupon/label/rateLabelMaker'
import SubtractionLabelMaker from './coupon/label/subtractionLabelMaker'
import Resource from './resource'

export class ApiCoupon extends Resource {
  /**
   * 商品を指定するためのIDとして、FKU IDを使用する。
   * https://github.com/my-color/front/issues/5157#issuecomment-842080668
   * https://github.com/my-color/front/issues/5157#issuecomment-842102143
   *
   * FKU IDを使用することは現状ではプロダクト全体で比較的安定していると考えられるので、ここでデフォルト値としておく。
   * このあたりの事情が流動化してきたら（e.g. ブランドやクーポンの種類によって、SKUを参照する）、getプロパティ化すること。
   */
  private readonly specificationIdPolicy: SpecificationIdPolicy = new SpecificationByFkuIdPolicy()

  /**
   * @return {boolean} 商品指定クーポンが指定した商品ならtrue、それ以外はfalse. 商品指定クーポンでない場合は常にfalse.
   */
  isSpecifiedFor(cartItem: CartItem): boolean {
    return this.specifiedItemIds.includes(
      this.specificationIdPolicy.getSpecificationId(cartItem)
    )
  }

  /**
   * @return {boolean} クーポンが適用可能なカート内商品ならtrueを、それ以外はfalseを返す
   */
  isApplicableFor(cartItem: CartItem): boolean {
    const specifiedItemRuleIsSatisfied = this.specifiedItemIds.length > 0
                                         ? this.isSpecifiedFor(cartItem)
                                         : true

    return (this.applicableToAny || this.applicableBrands.indexOf(cartItem.brandEnglishName || '') !== -1)
      && specifiedItemRuleIsSatisfied
      && !cartItem.isPreSales
      && !cartItem.ineligibleForCoupon
      && !cartItem.isNovelty
  }

  get calculator(): Calculator {
    switch (this.discountType) {
      case 'rate': return new RateCalculator(this.discountValue)
      case 'subtraction': return new SubtractionCalculator(this.discountValue)
      default: return new NoDiscountCalculator()
    }
  }

  get labelMaker(): LabelMaker {
    switch (this.discountType) {
      case 'rate': return new RateLabelMaker(this.discountValue)
      case 'subtraction': return new SubtractionLabelMaker(this.discountValue)
      default: return new NoDiscountLabelMaker()
    }
  }

  get title(): string {
    return this.get('title')
  }

  get imageUrl(): string {
    return this.get('aux_string_2')
  }

  get hasCouponCode(): boolean {
    const auxString5 = this.get('aux_string_5')

    return (auxString5 !== undefined) && (auxString5.length > 0)
  }

  get campaignId(): string {
    return this.get('coupon_context_id')
  }

  get isAvailable(): boolean {
    return !this.isUsed && this.isWithinValidPeriod && this.isVerified
  }

  get isUsed(): boolean {
    return this.get('shown')
  }

  get startAt(): Date {
    return new Date(this.get('start_at'))
  }

  get endAt(): Date {
    return new Date(this.get('end_at'))
  }

  get isWithinValidPeriod(): boolean {
    // TODO: memoize?
    const currentTime = CurrentTime.create()
    const now = currentTime.now

    return isAfterOrEqualTo(now, this.startAt) && isBeforeOrEqualTo(now, this.endAt)
  }

  get discountType(): DiscountType {
    return this.get('content.discount_type')
  }

  get discountValue(): number {
    return parseInt(this.get('content.discount_value'), 10)
  }

  // Because discount type/value need to be parsed from json string, the results are not reliable.
  get isVerified(): boolean {
    return !!this.discountType && !!this.discountValue && this.discountValueIsValid()
  }

  get statusLabel(): string {
    if (this.isUsed) {
      return '使用済み'
    }
    if (this.isWithinValidPeriod) {
      return '未使用'
    }

    return '期限切れ'
  }

  calculateDiscountPrice(originalPrice: number): number {
    return this.calculator.calculateDiscountPrice(originalPrice)
  }

  /**
   * クーポン割引適用対象商品の一覧
   *
   * クーポンごとに「指定の商品のみ割引」という設定がなされることがある.
   * この設定において、実際に指定されている商品IDの一覧.
   *
   * @see https://my-color.esa.io/posts/623 「商品指定」は何を単位とするか
   */
  get specifiedItemIds(): string[] {
    if (!this.isItemsSpecifiedCouponCode) return []

    /**
     * 空の場合は、デフォルト値として「クーポンが適用できる商品の配列は空」、つまり「クーポンはどの商品にも適用できない」と解釈する。
     */
    return this.get('content.ids', [])
  }
  appliableItemSpecified(): boolean {
    return this.specifiedItemIds.length > 0
  }
  private get isItemsSpecifiedCouponCode(): boolean {
    /**
     * クーポンの種類が商品指定か否かのフラグにより判定する。
     * なおフラグとは別に、具体的にどの商品が指定されているかを示す情報も存在する。
     * その情報に不備がある場合（キーが存在しない、キーは存在するが値がfalsy）に、商品指定クーポンコードとしてどう振る舞うかは別途定められる。
     */
    return this.get('content.discount_target') === 'ids'
  }

  private discountValueIsValid(): boolean {
    if (this.discountType === 'rate') {
      const ZERO = 0
      const HUNDRED = 100

      return this.discountValue >= ZERO && this.discountValue <= HUNDRED
    }

    return true
  }

  private get applicableBrands(): string[] {
    return this.get('applicable_brands', [])
  }
  private get applicableToAny(): boolean {
    return this.get('applicable_to_any', false)
  }
}

type DiscountType = '' | 'rate' | 'subtraction'

function isAfterOrEqualTo(test: Date, start: Date): boolean {
  return isEqual(test, start) || isAfter(test, start)
}

function isBeforeOrEqualTo(test: Date, start: Date): boolean {
  return isEqual(test, start) || isBefore(test, start)
}

/**
 * クーポンによるID指定の対象
 */
interface MaybeIdSpecified {
  readonly fkuId: string
}
/**
 * 商品指定というクーポンの適用可能性と、クーポンの指定方法(e.g. クーポンコードにより指定)は、直交する概念である。
 * したがって本来は、このクラスがさだめる商品指定周りのロジックを、CouponCodeから分離できるようにしておくのが望ましい。
 * ただしそのためには、
 * - ApiCouponに依存した既存の仕組みの手術が必要になるのと、
 * - いまのところ商品指定はクーポンコード形式でのみ実現される。ユーザに「どの商品には使えます」という情報を示す導線の関係と思われる。
 * という理由から、いったんは商品指定×クーポンコードという組合せをハードコードする仕方でこのクラスを定義しておく。
 *
 * 将来的には、いくつか手術の戦略が考えられる:
 * - 適用可能性と指定方法をそれぞれミックスインとして定義する。https://typescript-jp.gitbook.io/deep-dive/type-system/mixins
 * - 商品指定方法や指定方法を、基底クラスに埋めこむ。これは基底クラスが神クラス化するので避けたい。
 */
interface SpecificationIdPolicy {
  getSpecificationId(target: MaybeIdSpecified): string
}

class SpecificationByFkuIdPolicy implements SpecificationIdPolicy {
  getSpecificationId(target: MaybeIdSpecified): string {
    return target.fkuId
  }
}
